import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Dynamic import to avoid pulling i18next into the critical landing page bundle
async function loadLanguageDynamic(code: string) {
  const { loadLanguage } = await import('@/i18n');
  return loadLanguage(code);
}

function getStoredLanguage() {
  try {
    return localStorage.getItem('app_language') || 'en';
  } catch {
    return 'en';
  }
}

function persistLanguage(code: string) {
  try {
    localStorage.setItem('app_language', code);
  } catch {}
}

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
  flag: string;
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇺🇸' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', flag: '🇷🇺' },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', dir: 'ltr', flag: '🇬🇪' },
];

export const LANGUAGE_REGIONS: Record<string, string[]> = {
  'Global': ['en'],
  'Middle East': ['ar'],
  'Eastern Europe': ['ru'],
  'Caucasus': ['ka'],
};

export function getLanguageByCode(code: string): Language | undefined {
  return SUPPORTED_LANGUAGES.find(l => l.code === code);
}

export function getSuggestedLanguages(_country: string): Language[] {
  return SUPPORTED_LANGUAGES;
}

interface LanguageContextType {
  currentLanguage: Language;
  setLanguage: (code: string) => void;
  availableLanguages: Language[];
  isRTL: boolean;
  suggestedLanguages: Language[];
  setSuggestedByCountry: (country: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [currentLangCode, setCurrentLangCode] = useState<string>(() => {
    const stored = getStoredLanguage();
    return SUPPORTED_LANGUAGES.some(l => l.code === stored) ? stored : 'en';
  });

  const currentLanguage = getLanguageByCode(currentLangCode) || SUPPORTED_LANGUAGES[0];
  const isRTL = currentLanguage.dir === 'rtl';

  const setLanguage = useCallback((code: string) => {
    if (!SUPPORTED_LANGUAGES.some(l => l.code === code)) return;
    setCurrentLangCode(code);
    persistLanguage(code);
    loadLanguageDynamic(code);
  }, []);

  const setSuggestedByCountry = useCallback((_country: string) => {}, []);

  useEffect(() => {
    document.documentElement.dir = currentLanguage.dir;
    document.documentElement.lang = currentLanguage.code;
    if (currentLanguage.dir === 'rtl') {
      import('@/styles/rtl.css');
    }
    loadLanguageDynamic(currentLanguage.code);
  }, [currentLanguage]);

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      setLanguage,
      availableLanguages: SUPPORTED_LANGUAGES,
      isRTL,
      suggestedLanguages: [],
      setSuggestedByCountry,
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
